import type { Delivery, DeliveryStatus } from '@erp/shared';
import { Camera, Check, MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, EmptyState } from '../../components/ui/index.js';
import { listMyDeliveries, setMyDeliveryStatus, uploadDeliveryProof } from '../../lib/delivery.js';

const TONE: Record<DeliveryStatus, 'secondary' | 'info' | 'warning' | 'success' | 'danger'> = {
  pending: 'secondary', assigned: 'info', out_for_delivery: 'warning', delivered: 'success', failed: 'danger',
};
const DRIVER_STEPS: DeliveryStatus[] = ['out_for_delivery', 'delivered', 'failed'];
const STEP_KEY: Record<string, string> = { out_for_delivery: 'detail.markOut', delivered: 'detail.markDelivered', failed: 'detail.markFailed' };
const PHOTO_MIME = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const PHOTO_MAX = 8 * 1024 * 1024;

export function MyDeliveriesPage(): JSX.Element {
  const { t } = useTranslation('delivery');
  const [rows, setRows] = useState<Delivery[] | null>(null);

  const reload = (): void => { listMyDeliveries().then(setRows).catch(() => setRows([])); };
  useEffect(reload, []);

  const patch = (d: Delivery): void => setRows((prev) => (prev ?? []).map((r) => (r.id === d.id ? d : r)));

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('my.heading')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('my.subtitle')}</p>
      </header>

      {!rows ? (
        <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>
      ) : rows.length === 0 ? (
        <EmptyState title={t('my.empty')} />
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((d) => <DriverCard key={d.id} delivery={d} onChange={patch} />)}
        </div>
      )}
    </div>
  );
}

function DriverCard({ delivery: d, onChange }: { delivery: Delivery; onChange: (d: Delivery) => void }): JSX.Element {
  const { t } = useTranslation('delivery');
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const mapsUrl = d.latitude && d.longitude ? `https://maps.google.com/?q=${d.latitude},${d.longitude}` : null;

  const onStatus = async (status: DeliveryStatus): Promise<void> => {
    const failureReason = status === 'failed' ? (window.prompt(t('detail.failureReason')) ?? undefined) : undefined;
    setBusy(true);
    try { onChange(await setMyDeliveryStatus(d.id, { status, failureReason })); toast.success(t('actions.statusUpdated')); }
    catch { toast.error(t('actions.failed')); } finally { setBusy(false); }
  };

  const onFile = async (file: File): Promise<void> => {
    if (!PHOTO_MIME.has(file.type) || file.size > PHOTO_MAX) { toast.error(t('actions.badPhoto')); return; }
    setBusy(true);
    try { onChange(await uploadDeliveryProof(d.id, file, 'mine')); toast.success(t('actions.proofUploaded')); }
    catch { toast.error(t('actions.failed')); } finally { setBusy(false); }
  };

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono font-semibold">{d.docNumber}</span>
        <Badge variant={TONE[d.status]}>{t(`status.${d.status}`)}</Badge>
      </div>
      <div className="text-sm">
        <div className="font-medium">{d.recipientName ?? '—'}</div>
        {d.phone && <a href={`tel:${d.phone}`} className="text-brand hover:underline">{d.phone}</a>}
        {(d.addressLine1 || d.city) && <div className="text-fg-muted">{[d.addressLine1, d.city].filter(Boolean).join(', ')}</div>}
        {mapsUrl && (
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-brand hover:underline">
            <MapPin className="h-3.5 w-3.5" aria-hidden />{t('my.openMap')}
          </a>
        )}
      </div>
      <input ref={inputRef} type="file" accept={[...PHOTO_MIME].join(',')} capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); if (e.target) e.target.value = ''; }} />
      <div className="flex flex-wrap gap-1.5">
        {DRIVER_STEPS.map((s) => (
          <Button key={s} size="sm" variant={s === 'delivered' ? undefined : 'secondary'} disabled={busy || s === d.status} onClick={() => void onStatus(s)}>
            {t(STEP_KEY[s] ?? '')}
          </Button>
        ))}
        <Button size="sm" variant="secondary" loading={busy} onClick={() => inputRef.current?.click()}>
          {d.proofAttachmentId ? <Check className="h-4 w-4" aria-hidden /> : <Camera className="h-4 w-4" aria-hidden />}
          {t('detail.uploadProof')}
        </Button>
      </div>
    </Card>
  );
}
